import { PointCloudOctree } from 'potree-core'
import { EMPTY, forkJoin, Observable, of } from 'rxjs'
import { catchError, map, switchMap } from 'rxjs/operators'
import * as THREE from 'three'
import { DDSLoader } from 'three/examples/jsm/loaders/DDSLoader.js'
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { MTLLoader } from 'three/examples/jsm/loaders/MTLLoader.js'
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js'
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader.js'
import { TGALoader } from 'three/examples/jsm/loaders/TGALoader.js'

import { Injectable } from '@angular/core'
import * as GaussianSplats3d from '@mkkellogg/gaussian-splats-3d'
import { S3FileAccessContext } from '@services/aws.service'
import { AuthenticationService } from '@services/authentication.service'
import { FeatureService } from '@services/feature.service'
import { FileReferenceService } from '@services/file-reference.service'
import { ModelService } from '@services/model.service'
import { PointCloudService } from '@services/point-cloud.service'
import { ProjectService } from '@services/project.service'
import { ToastService } from '@services/toast.service'

import { FileReference } from './FileReference'
import { ModelFile } from './Model'

/** Strips '.obj' and '.mtl' from filenames. Returns filename without ext. */
function stripObjMtlExt(fn: string) {
    if (!fn.endsWith('.mtl') && !fn.endsWith('.obj')) throw new Error(`Filename ${fn} was not .obj or .mtl.`)
    return fn.slice(0, -4)
}

/** Extensions the S3 loaders below open as a model in their own right (obj/mtl, glTF, splat). */
const S3_MODEL_FILE_EXTENSIONS = ['.obj', '.mtl', '.gltf', '.glb', '.ply', '.splat', '.ksplat', '.spz']

/**
 * True for a file one of the S3 loaders opens as a model, as opposed to a texture or buffer that a model
 * file references. Those resources can't be told apart by extension, so a loader has to keep anything
 * that isn't some format's model file — but never needs another format's model files.
 */
function isS3ModelFile(file: ModelFile) {
    const key = file.key.toLowerCase()
    return S3_MODEL_FILE_EXTENSIONS.some(extension => key.endsWith(extension))
}

/** Loads protected raster textures through a header-capable request before browser image decoding. */
class AuthenticatedTextureLoader extends THREE.Loader<THREE.Texture> {
    public load(
        url: string,
        onLoad?: (texture: THREE.Texture) => void,
        onProgress?: (event: ProgressEvent) => void,
        onError?: (error: unknown) => void,
    ): THREE.Texture {
        const texture = new THREE.Texture()
        const fileLoader = new THREE.FileLoader(this.manager)
            .setPath(this.path)
            .setWithCredentials(this.withCredentials)
            .setRequestHeader(this.requestHeader)
            .setResponseType('blob')

        fileLoader.load(url, data => {
            const objectUrl = URL.createObjectURL(data as unknown as Blob)
            new THREE.ImageLoader(this.manager)
                .setCrossOrigin(this.crossOrigin)
                .load(objectUrl, image => {
                    texture.image = image
                    texture.needsUpdate = true
                    URL.revokeObjectURL(objectUrl)
                    onLoad?.(texture)
                }, undefined, error => {
                    URL.revokeObjectURL(objectUrl)
                    onError?.(error)
                })
        }, onProgress, onError)

        return texture
    }
}

@Injectable({
    providedIn: 'root'
})
export class FileReferenceLoader {
    private _dracoLoader: DRACOLoader = new DRACOLoader()
        .setCrossOrigin('*')
        .setDecoderPath('https://raw.githubusercontent.com/mrdoob/three.js/dev/examples/jsm/libs/draco/')
        .preload()

    constructor(
        private _authenticationService: AuthenticationService,
        private _fileReferenceService: FileReferenceService,
        private _featureService: FeatureService,
        private _modelService: ModelService,
        private _pointCloudService: PointCloudService,
        private _projectService: ProjectService,
        private _toastService: ToastService
    ) { }

    /**
     * Given some FileReferences, for each obj file, load the files and emit the groups.
     * @returns An Observable which emits a THREE.Group each time an obj file is loaded
     * and completes when there are no more obj files to load.
     * */
    public objLoader$(files: FileReference[], modelOrProjectID: number) {
        return this._newManager(files, modelOrProjectID,
            { regex: /\.dds$/i, loader: new DDSLoader() },
            { regex: /\.tga$/i, loader: new TGALoader() }
        ).pipe(
            switchMap(({ manager, requestHeader }) => {
                let objLoader = new OBJLoader(manager).setRequestHeader(requestHeader)

                // IMPORTANT NOTE: Not handling FileReferences with duplicate filenames (including their extensions, of course)
                const objFiles = files.filter(f => f.extension == '.obj')
                const mtlFiles = files.filter(f => f.extension == '.mtl')
                const unpairedObjs = objFiles.filter(obj => !mtlFiles.some(mtl => stripObjMtlExt(mtl.filename) == stripObjMtlExt(obj.filename)))

                return new Observable<THREE.Group>((obs) => {
                    if (unpairedObjs.length == 0) {
                        obs.complete()
                        return
                    }

                    const defaultMaterial = new THREE.MeshPhongMaterial({
                        color: 0x808080,
                        shininess: 100,
                        side: THREE.DoubleSide
                    })

                    // Load objs WITHOUT mtls
                    for (let i = 0; i < unpairedObjs.length; i++) {
                        objLoader.load(unpairedObjs[i].filename, (group) => {
                            group.traverse(child => {
                                if (child.type == 'Mesh') {
                                    let mesh: THREE.Mesh = child as THREE.Mesh
                                    mesh.material = defaultMaterial
                                    child.receiveShadow = true
                                }
                            })

                            group.name = unpairedObjs[i].filename
                            obs.next(group)
                            if (i == unpairedObjs.length - 1) obs.complete()
                        }, () => { }, (err) => { obs.error(err) })
                    }
                })
            }),
            catchError((err) => {
                this._toastService.toast({ title: 'Error', message: 'Model missing file: ' + err, color: 'red' })
                return EMPTY
            })
        )
    }

    /**
     * Given some FileReferences, for each .mtl file with corresponding .obj file,
     * load the file pairs and emit the groups.
     * @returns An Observable which emits a THREE.Group each time a file pair is loaded
     * and completes when there are no more pairs to load.
     * */
    public mtlObjLoader$(files: FileReference[], modelOrProjectID: number) {
        return this._newManager(files, modelOrProjectID,
            { regex: /\.dds$/i, loader: new DDSLoader() },
            { regex: /\.tga$/i, loader: new TGALoader() }
        ).pipe(
            switchMap(({ manager, requestHeader }) => {
                let mtlLoader = new MTLLoader(manager).setRequestHeader(requestHeader)

                // IMPORTANT NOTE: Not handling FileReferences with duplicate filenames (including their extensions, of course)
                const objFiles = files.filter(f => f.extension == '.obj')
                const mtlFiles = files.filter(f => f.extension == '.mtl')
                let objMtlPairs: { obj: FileReference, mtl: FileReference }[] = []

                mtlFiles.forEach(mtl => {
                    let obj = objFiles.find(obj => stripObjMtlExt(obj.filename) == stripObjMtlExt(mtl.filename))
                    if (obj) objMtlPairs.push({ obj: obj, mtl: mtl })
                })

                return new Observable<THREE.Group>((obs) => {
                    if (objMtlPairs.length == 0) {
                        obs.complete()
                        return
                    }

                    // Load mtls WITH objs
                    for (let i = 0; i < objMtlPairs.length; i++) {
                        let obj = objMtlPairs[i].obj
                        let mtl = objMtlPairs[i].mtl

                        mtlLoader.load(mtl.filename, (materials) => {
                            materials.preload()
                            new OBJLoader(manager).setRequestHeader(requestHeader).setMaterials(materials).load(obj.filename, (group) => {
                                group.name = obj.filename
                                obs.next(group)
                                if (i == objMtlPairs.length - 1) obs.complete()
                            }, () => { }, (err) => { obs.error(err) })
                        }, () => { }, (err) => { obs.error(err) })
                    }
                })
            }),
            catchError((err) => {
                this._toastService.toast({ title: 'Error', message: 'Model missing file: ' + err, color: 'red' })
                return EMPTY
            })
        )
    }

    /**
     * Given some FileReferences, for each .gltf or .glb file, load the files and emit the groups.
     * @returns An Observable which emits a THREE.Group each time a file is loaded
     * and completes when there are no more files to load.
     * */
    public gltfLoader$(files: FileReference[], modelOrProjectID: number) {
        return this._newManager(files, modelOrProjectID).pipe(
            switchMap(({ manager, requestHeader }) => {
                const gltfLoader: GLTFLoader = new GLTFLoader(manager)
                    .setRequestHeader(requestHeader)
                    .setDRACOLoader(this._dracoLoader)
                const gltfFiles = files.filter(({ extension: ext }) => ext == '.gltf' || ext == '.glb')

                return new Observable<THREE.Group>((obs) => {
                    if (gltfFiles.length == 0) {
                        obs.complete()
                        return
                    }

                    for (let i = 0; i < gltfFiles.length; i++) {
                        gltfLoader.load(gltfFiles[i].filename, (gltfOrGLB) => {
                            let parent = new THREE.Group()
                            gltfOrGLB.scenes.forEach(scene => parent.add(scene))
                            parent.name = gltfFiles[i].filename
                            obs.next(parent)
                            if (i == gltfFiles.length - 1) obs.complete()
                        }, () => { }, (err) => { obs.error(err) })
                    }
                })
            }),
            catchError((err) => {
                this._toastService.toast({ title: 'Error', message: 'Model missing file: ' + err, color: 'red' })
                return EMPTY
            })
        )
    }

    public stlLoader$(files: FileReference[], modelOrProjectID: number) {
        return this._newManager(files, modelOrProjectID).pipe(
            switchMap(({ manager, requestHeader }) => {
                const stlLoader: STLLoader = new STLLoader(manager).setRequestHeader(requestHeader)
                const stlFiles = files.filter(({ extension: ext }) => ext == '.stl')

                return new Observable<THREE.Group>((obs) => {
                    if (stlFiles.length == 0) {
                        obs.complete()
                        return
                    }

                    const defaultMaterial = new THREE.MeshPhongMaterial({
                        color: 0xaaaaaa,
                        side: THREE.DoubleSide
                    })

                    for (let i = 0; i < stlFiles.length; i++) {
                        stlLoader.load(stlFiles[i].filename, (geometry) => {
                            const mesh = new THREE.Mesh(geometry, defaultMaterial)

                            const group = new THREE.Group()
                            group.add(mesh)
                            group.name = stlFiles[i].filename

                            obs.next(group)
                            if (i == stlFiles.length - 1) obs.complete()
                        }, () => { }, (err) => { obs.error(err) })
                    }
                })
            }),
            catchError((err) => {
                this._toastService.toast({ title: 'Error', message: 'Model missing file: ' + err, color: 'red' })
                return EMPTY
            })
        )
    }

    public pointCloudLoader$(params: { featureID?: number, modelID?: number }): Observable<THREE.Group> {
        if (params.featureID) {
            var getPointCloudFiles = this._featureService.getPointCloudFiles(params.featureID)
        } else if (params.modelID) {
            var getPointCloudFiles = this._modelService.getPointCloudFiles(params.modelID)
        } else {
            return EMPTY
        }

        return getPointCloudFiles.pipe(
            switchMap(({ metadataURL, octreeURL, hierarchyURL }) => {
                return new Observable<any>((obs) => {
                    this._pointCloudService.potreeInstance.loadPointCloud("metadata.json", octreeURL, hierarchyURL, () => metadataURL)
                        .then((pointCloud: PointCloudOctree) => {
                            pointCloud.material.depthTest = true
                            pointCloud.material.depthWrite = true
                            pointCloud.material.opacity = 1
                            // EXPERIMENT 10: High minSize floor for uniform appearance
                            // ATTENUATED still scales by distance, but minSize creates a FLOOR
                            // so distant (sparse) points won't shrink below this threshold
                            pointCloud.material.size = 4              // Base size (was 2)
                            pointCloud.material.minSize = 5           // HIGH FLOOR - distant points stay 5px minimum
                            pointCloud.material.maxSize = 8           // Closer points only slightly larger
                            pointCloud.material.inputColorEncoding = 1
                            pointCloud.material.outputColorEncoding = 1
                            pointCloud.material.shape = 1             // Circles blend better than squares
                            // Keep ATTENUATED - only working option
                            pointCloud.material.pointSizeType = 1     // ATTENUATED

                                // LOD settings for EXPERIMENT 10:
                                ; (pointCloud as any).minNodePixelSize = 30
                                ; (pointCloud as any).maxLevel = 20

                            this._pointCloudService.addPointCloud(pointCloud)
                            pointCloud.userData.type = "point-cloud"

                            // Note: We preserve the point cloud's original position from Potree.
                            // For Map scenes, SceneManager will reset it to [0,0,0] because those scenes
                            // use Mercator coordinates via updateOrientation().
                            // For Standard/Virtual Tour scenes, we keep the original position so that
                            // the centering algorithm in applyModelsTransformations works correctly.

                            const group = new THREE.Group().add(pointCloud)

                            obs.next(group)
                            obs.complete()
                        })
                    // .catch(e => console.warn(e)) as Promise<THREE.Group>
                })
            })
        )
    }

    public splatLoader$(files: FileReference[], modelOrProjectID: number, camera: THREE.Camera, renderer: THREE.WebGLRenderer): Observable<THREE.Group<THREE.Object3DEventMap>> {
        const splatFile = files[0]
        if (!splatFile) return EMPTY
        let format

        if (splatFile.extension === '.ksplat') {
            format = GaussianSplats3d.SceneFormat.KSplat
        } else if (splatFile.extension === '.ply') {
            format = GaussianSplats3d.SceneFormat.Ply
        } else if (splatFile.extension === '.splat') {
            format = GaussianSplats3d.SceneFormat.Splat
        } else if (splatFile.extension === '.spz') {
            format = GaussianSplats3d.SceneFormat.Spz
        }

        if (format == null) return EMPTY

        return this._authenticationService.getToken().pipe(
            catchError(() => {
                this._toastService.toast({
                    title: 'Error',
                    message: 'Could not access splat file',
                    color: 'red'
                })
                return EMPTY
            }),
            map((token: string) => {
                const url = this._fileReferenceService.getUrlForFileDownload(
                    splatFile.id,
                    modelOrProjectID,
                    splatFile.hash,
                    token,
                    splatFile
                )

                // Create the viewer
                const viewer = new GaussianSplats3d.DropInViewer({
                    renderer: renderer,
                    camera: camera,
                    sharedMemoryForWorkers: false,
                    useBuiltInControls: false,
                    enableOptionalEffects: true
                })

                // Add the splat to the viewer
                viewer.addSplatScene(url, {
                    format: format,
                    splatAlphaRemovalThreshold: 5,
                    showLoadingUI: false,
                    position: [0, 0, 0],
                    rotation: [1, 0, 0, 0],
                    progressiveLoad: false,
                    scale: [1, 1, 1]
                }).then(() => {
                    return new Promise(resolve => requestAnimationFrame(resolve)) // Make it wait a frame before adding collision mesh so the initial camera position is close up and not far away
                }).then(() => {
                    this.createPreciseCollisionMesh(viewer)
                })

                const group = new THREE.Group<THREE.Object3DEventMap>()
                if (viewer) {
                    group.add(viewer)
                }
                return group
            }),
            catchError((err) => {
                this._toastService.toast({
                    title: 'Error',
                    message: 'Error Loading Splat Model',
                    color: 'red'
                })
                return EMPTY
            })
        )
    }

    private createPreciseCollisionMesh(viewer: GaussianSplats3d.DropInViewer) {
        const splatMesh = viewer.splatMesh
        if (!splatMesh) return

        const splatCount = splatMesh.getSplatCount()
        const vertices = []
        const indices = []

        // Extract splat data to build geometry
        const centers = new Float32Array(splatCount * 3)
        const scales = new Float32Array(splatCount * 3)
        const rotations = new Float32Array(splatCount * 4)

        // Fill arrays with splat data
        splatMesh.fillSplatDataArrays(null, scales, rotations, centers, null, null, true)

        // Create geometry from splat data
        const points: THREE.Vector3[] = []
        for (let i = 0; i < splatCount; i++) {
            const base = i * 3
            points.push(new THREE.Vector3(
                centers[base],
                centers[base + 1],
                centers[base + 2]
            ))
        }

        // Spatially group the splats
        const gridSize = 4 // Controls quantity and size of boxes, 4 seems to be a good balance between precision and performance
        const clusters: Map<string, THREE.Vector3[]> = new Map()

        points.forEach(point => {
            const key = [
                Math.floor(point.x / gridSize),
                Math.floor(point.y / gridSize),
                Math.floor(point.z / gridSize)
            ].join(',')
            if (!clusters.has(key)) clusters.set(key, [])
            clusters.get(key)!.push(point)
        })

        // Create one box per cluster
        let clusterIndex = 0
        clusters.forEach((clusterPoints) => {
            // Compute bounding box
            const box = new THREE.Box3().setFromPoints(clusterPoints)
            const center = box.getCenter(new THREE.Vector3())
            const size = box.getSize(new THREE.Vector3()).multiplyScalar(1.1) // pad a bit
            const rotation = new THREE.Quaternion() // identity rotation

            this.addEllipsoidToGeometry(center, size, rotation, vertices, indices, vertices.length / 3)
            clusterIndex++
        })

        // Create the mesh
        const geometry = new THREE.BufferGeometry()
        geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3))
        geometry.setIndex(indices)
        geometry.computeVertexNormals()

        const customCallbackMaterial = new THREE.MeshBasicMaterial({
            colorWrite: false,   // flip to see geometry
            depthWrite: false,   // flip to see geometry
            transparent: true,   // flip to see geometry
            opacity: 0
        })

        const customCallbackMesh = new THREE.Mesh(geometry, customCallbackMaterial)
        customCallbackMesh.frustumCulled = false

        // Replace the default callback mesh
        viewer.remove(viewer.callbackMesh)
        viewer.callbackMesh = customCallbackMesh
        viewer.add(customCallbackMesh)
        customCallbackMesh.onBeforeRender = viewer.constructor.onBeforeRender.bind(viewer, viewer.viewer)
    }

    private addEllipsoidToGeometry(center: THREE.Vector3, scale: THREE.Vector3, rotation: THREE.Quaternion, vertices: number[], indices: number[], startIndex: number) {
        // Create a simplified ellipsoid using 8 vertices (box approximation)
        const halfScale = scale.clone().multiplyScalar(0.5)

        const corners = [
            new THREE.Vector3(-halfScale.x, -halfScale.y, -halfScale.z),
            new THREE.Vector3(halfScale.x, -halfScale.y, -halfScale.z),
            new THREE.Vector3(halfScale.x, halfScale.y, -halfScale.z),
            new THREE.Vector3(-halfScale.x, halfScale.y, -halfScale.z),
            new THREE.Vector3(-halfScale.x, -halfScale.y, halfScale.z),
            new THREE.Vector3(halfScale.x, -halfScale.y, halfScale.z),
            new THREE.Vector3(halfScale.x, halfScale.y, halfScale.z),
            new THREE.Vector3(-halfScale.x, halfScale.y, halfScale.z)
        ]

        // Apply rotation and translation
        corners.forEach(corner => {
            corner.applyQuaternion(rotation)
            corner.add(center)
            vertices.push(corner.x, corner.y, corner.z)
        })

        // Add indices for box faces
        const faceIndices = [
            0, 1, 2, 0, 2, 3, // front
            4, 7, 6, 4, 6, 5, // back
            0, 4, 5, 0, 5, 1, // bottom
            2, 6, 7, 2, 7, 3, // top
            0, 3, 7, 0, 7, 4, // left
            1, 5, 6, 1, 6, 2  // right
        ]

        faceIndices.forEach(index => indices.push(startIndex + index))
    }

    /**
     * A manager takes a loaders request to a file and formats it as a call to our API.
     * @param handlers We are not sure we even need handlers.
     * We do not know how they work. They came from an example long ago.
     */
    private _newManager(fileReferences: FileReference[], modelOrProjectID: number, ...handlers: { regex: RegExp, loader: THREE.Loader }[]) {
        return this._authenticationService.getToken().pipe(
            map(token => {
                const manager = new THREE.LoadingManager().setURLModifier(filename => {
                    if (filename.startsWith('blob')) {
                        return filename
                    } else {
                        // Ensure, if an absolute path is asked for, only the file name is used
                        if (filename.indexOf("/") != -1) filename = filename.split("/").pop()
                        if (filename.indexOf("\\") != -1) filename = filename.split("\\").pop()
                    }
                    // Now, use the filename to find the file reference id
                    // IMPORTANT NOTE: Assuming all of the filenames are unique for this model
                    const fileReference = fileReferences.find(fileReference => fileReference.filename == filename)

                    if (!fileReference) throw filename

                    const { id: fileReferenceID, hash } = fileReference

                    return this._fileReferenceService.getUrlForFileDownload(fileReferenceID, modelOrProjectID, hash)
                })

                const requestHeader: Record<string, string> = token
                    ? { Authorization: `Bearer ${token}` }
                    : {}

                if (token) {
                    manager.addHandler(
                        /\.(?:avif|bmp|gif|jpe?g|png|svg|webp)(?:[?#].*)?$/i,
                        new AuthenticatedTextureLoader(manager).setRequestHeader(requestHeader),
                    )
                }

                handlers.forEach(handler => {
                    handler.loader.manager = manager
                    handler.loader.setRequestHeader(requestHeader)
                    manager.addHandler(handler.regex, handler.loader)
                })

                return { manager, requestHeader }
            })
        )
    }

    /**
     * Creates a THREE.LoadingManager for S3 files.
     * We do not know how they work. They came from an example long ago.
     *
     * Every URL this manager hands back has to be self-authenticating: THREE fetches each file (and
     * each texture that file references) itself, outside Angular's HttpClient, so the auth-header
     * interceptor never runs and no bearer token can be attached to those requests. setURLModifier
     * must also answer synchronously, so each file's presigned URL is resolved up front and the
     * modifier only does a lookup.
     *
     * Breadcrumb: FileReferenceService.getS3FileUrlForProject forwards explicit model/project context
     * to the backend/index.ts contextual presign routes. getS3FileUrl verifies resource permission,
     * exact reference membership and resource sign-in policy before signing the DB-selected object.
     * Public viewer projects retain the existing public file-stream route.
     *
     * Callers should pass only the files their loader can request, since every file passed costs a
     * presign request. They must also only build a manager when they actually have files to load,
     * because a manager for an empty file list never emits.
     */
    private _newS3Manager(s3Files: ModelFile[], context: S3FileAccessContext | undefined, ...handlers: { regex: RegExp, loader: THREE.Loader }[]) {
        const presignedUrls$ = s3Files.map(file =>
            this._fileReferenceService.getS3FileUrlForProject(file.s3ObjectReferenceId, context ?? { kind: 'model', id: file.modelID }).pipe(
                map(url => [file.s3ObjectReferenceId, url] as [number, string | null]),
                // One file failing to presign must not take the rest of the model down with it. Remember
                // the failure, and let the URL modifier report the file missing only if a loader asks for it.
                catchError(() => of([file.s3ObjectReferenceId, null] as [number, string | null]))
            )
        )

        return forkJoin(presignedUrls$).pipe(
            map(presignedUrls => {
                const urlByS3ObjectReferenceId = new Map<number, string | null>(presignedUrls)

                // Build a basename → ModelFile[] index for fast lookup, handling collisions
                const basenameIndex = new Map<string, ModelFile[]>()
                for (const f of s3Files) {
                    const base = f.key.split('/').pop()!
                    if (!basenameIndex.has(base)) basenameIndex.set(base, [])
                    basenameIndex.get(base)!.push(f)
                }

                const manager = new THREE.LoadingManager().setURLModifier(filename => {
                    if (filename.startsWith('blob')) {
                        return filename
                    }

                    // Preserve original path for suffix matching before stripping to basename
                    const originalFilename = filename
                    if (filename.indexOf("/") != -1) filename = filename.split("/").pop()
                    if (filename.indexOf("\\") != -1) filename = filename.split("\\").pop()

                    // Look up candidates by basename
                    const candidates = basenameIndex.get(filename)
                    if (!candidates || candidates.length === 0) throw filename

                    let s3File: ModelFile
                    if (candidates.length === 1) {
                        // Unique basename — no collision
                        s3File = candidates[0]
                    } else {
                        // Basename collision: prefer key whose suffix matches the original path
                        s3File = candidates.find(f => f.key.endsWith(originalFilename)) ?? candidates[0]
                    }

                    // A file that failed to presign is handled exactly like one the model doesn't have
                    const url = urlByS3ObjectReferenceId.get(s3File.s3ObjectReferenceId)
                    if (!url) throw filename

                    return url
                })

                handlers.forEach(handler => {
                    handler.loader.manager = manager
                    manager.addHandler(handler.regex, handler.loader)
                })

                return manager
            })
        )
    }

    /**
     * Given some S3 ModelFiles, for each .obj file, load the files and emit the groups.
     * @returns An Observable which emits a THREE.Group each time an obj file is loaded
     * and completes when there are no more obj files to load.
     * */
    public objLoaderS3$(s3Files: ModelFile[], context?: S3FileAccessContext): Observable<THREE.Group> {
        // IMPORTANT NOTE: Not handling S3 files with duplicate filenames (including their extensions, of course)
        const objFiles = s3Files.filter(f => f.key.endsWith('.obj'))
        const mtlFiles = s3Files.filter(f => f.key.endsWith('.mtl'))
        const unpairedObjs = objFiles.filter(obj => !mtlFiles.some(mtl => stripObjMtlExt(mtl.key.split('/').pop()!) == stripObjMtlExt(obj.key.split('/').pop()!)))

        // Bail before the manager presigns URLs for a model this loader has nothing to load from
        if (unpairedObjs.length == 0) return EMPTY

        // An .obj without an .mtl references no other file (OBJLoader ignores mtllib), so presign only these
        return this._newS3Manager(unpairedObjs, context,
            { regex: /\.dds$/i, loader: new DDSLoader() },
            { regex: /\.tga$/i, loader: new TGALoader() }
        ).pipe(
            switchMap(manager => {
                let objLoader = new OBJLoader(manager)

                return new Observable<THREE.Group>((obs) => {
                    for (let i = 0; i < unpairedObjs.length; i++) {
                        const filename = unpairedObjs[i].key.split('/').pop()!
                        objLoader.load(filename, (obj) => {
                            obj.name = filename
                            obs.next(obj)
                            if (i == unpairedObjs.length - 1) obs.complete()
                        }, () => { }, (err) => { obs.error(err) })
                    }
                })
            }),
            catchError((err) => {
                this._toastService.toast({ title: 'Error', message: typeof err === 'string' && s3Files.some(file => file.key.split('/').pop() === err)
                    ? 'Model missing file: ' + err : 'Could not load model file.', color: 'red' })
                return EMPTY
            })
        )
    }

    /**
     * Given some S3 ModelFiles, for each .mtl file with corresponding .obj file,
     * load the file pairs and emit the groups.
     * @returns An Observable which emits a THREE.Group each time a file pair is loaded
     * and completes when there are no more pairs to load.
     * */
    public mtlObjLoaderS3$(s3Files: ModelFile[], context?: S3FileAccessContext): Observable<THREE.Group> {
        // IMPORTANT NOTE: Not handling S3 files with duplicate filenames (including their extensions, of course)
        const objFiles = s3Files.filter(f => f.key.endsWith('.obj'))
        const mtlFiles = s3Files.filter(f => f.key.endsWith('.mtl'))
        let objMtlPairs: { obj: ModelFile, mtl: ModelFile }[] = []

        mtlFiles.forEach(mtl => {
            let obj = objFiles.find(obj => stripObjMtlExt(obj.key.split('/').pop()!) == stripObjMtlExt(mtl.key.split('/').pop()!))
            if (obj) objMtlPairs.push({ obj: obj, mtl: mtl })
        })

        // Bail before the manager presigns URLs for a model this loader has nothing to load from
        if (objMtlPairs.length == 0) return EMPTY

        // Presign the pairs plus anything their .mtl files could reference as a texture
        const pairedFiles = objMtlPairs.flatMap(({ obj, mtl }) => [obj, mtl])
        return this._newS3Manager(s3Files.filter(f => pairedFiles.includes(f) || !isS3ModelFile(f)), context,
            { regex: /\.dds$/i, loader: new DDSLoader() },
            { regex: /\.tga$/i, loader: new TGALoader() }
        ).pipe(
            switchMap(manager => {
                let mtlLoader = new MTLLoader(manager)

                return new Observable<THREE.Group>((obs) => {
                    for (let i = 0; i < objMtlPairs.length; i++) {
                        const mtlFilename = objMtlPairs[i].mtl.key.split('/').pop()!
                        const objFilename = objMtlPairs[i].obj.key.split('/').pop()!

                        mtlLoader.load(mtlFilename, (materials) => {
                            materials.preload()
                            let objLoader = new OBJLoader(manager)
                            objLoader.setMaterials(materials)
                            objLoader.load(objFilename, (obj) => {
                                obj.name = objFilename
                                obs.next(obj)
                                if (i == objMtlPairs.length - 1) obs.complete()
                            }, () => { }, (err) => { obs.error(err) })
                        }, () => { }, (err) => { obs.error(err) })
                    }
                })
            }),
            catchError((err) => {
                this._toastService.toast({ title: 'Error', message: typeof err === 'string' && s3Files.some(file => file.key.split('/').pop() === err)
                    ? 'Model missing file: ' + err : 'Could not load model file.', color: 'red' })
                return EMPTY
            })
        )
    }

    /**
     * Given some S3 ModelFiles, for each .gltf or .glb file, load the files and emit the groups.
     * @returns An Observable which emits a THREE.Group each time a file is loaded
     * and completes when there are no more files to load.
     * */
    public gltfLoaderS3$(s3Files: ModelFile[], context?: S3FileAccessContext): Observable<THREE.Group> {
        const gltfFiles = s3Files.filter(({ key }) => key.endsWith('.gltf') || key.endsWith('.glb'))

        // Bail before the manager presigns URLs for a model this loader has nothing to load from
        if (gltfFiles.length == 0) return EMPTY

        // Presign the glTF files plus anything they could reference as a buffer or texture
        return this._newS3Manager(s3Files.filter(f => gltfFiles.includes(f) || !isS3ModelFile(f)), context).pipe(
            switchMap(manager => {
                const gltfLoader: GLTFLoader = new GLTFLoader(manager).setDRACOLoader(this._dracoLoader)

                return new Observable<THREE.Group>((obs) => {
                    for (let i = 0; i < gltfFiles.length; i++) {
                        const filename = gltfFiles[i].key.split('/').pop()!
                        gltfLoader.load(filename, (gltfOrGLB) => {
                            let parent = new THREE.Group()
                            gltfOrGLB.scenes.forEach(scene => parent.add(scene))
                            parent.name = filename
                            obs.next(parent)
                            if (i == gltfFiles.length - 1) obs.complete()
                        }, () => { }, (err) => { obs.error(err) })
                    }
                })
            }),
            catchError((err) => {
                this._toastService.toast({ title: 'Error', message: typeof err === 'string' && s3Files.some(file => file.key.split('/').pop() === err)
                    ? 'Model missing file: ' + err : 'Could not load model file.', color: 'red' })
                return EMPTY
            })
        )
    }

    /**
     * Given some S3 ModelFiles, for each splat file, load the files and emit the groups.
     * @returns An Observable which emits a THREE.Group each time a splat file is loaded
     * and completes when there are no more splat files to load.
     * */
    public splatLoaderS3$(s3Files: ModelFile[], camera: THREE.Camera, renderer: THREE.WebGLRenderer, context?: S3FileAccessContext): Observable<THREE.Group<THREE.Object3DEventMap>> {
        const splatFile = s3Files[0]
        if (!splatFile) return EMPTY
        let format

        const extension = splatFile.key.split('.').pop()?.toLowerCase()
        if (extension === 'ksplat') {
            format = GaussianSplats3d.SceneFormat.KSplat
        } else if (extension === 'ply') {
            format = GaussianSplats3d.SceneFormat.Ply
        } else if (extension === 'splat') {
            format = GaussianSplats3d.SceneFormat.Splat
        } else if (extension === 'spz') {
            format = GaussianSplats3d.SceneFormat.Spz
        }

        if (format == null) return EMPTY

        // Note: DropInViewer fetches this URL itself, bypassing Angular's HttpClient (and its
        // auth-header interceptor), so it must be a presigned URL that's already self-authenticating
        // -- it cannot rely on a bearer token being attached to the request afterward.
        return this._fileReferenceService.getS3FileUrlForProject(splatFile.s3ObjectReferenceId, context ?? { kind: 'model', id: splatFile.modelID }).pipe(
            catchError(() => {
                this._toastService.toast({
                    title: 'Error',
                    message: 'Could not access splat file',
                    color: 'red'
                })
                return EMPTY
            }),
            map((url: string) => {
                // Create the viewer
                const viewer = new GaussianSplats3d.DropInViewer({
                    renderer: renderer,
                    camera: camera,
                    sharedMemoryForWorkers: false,
                    useBuiltInControls: false
                })

                // Add the splat to the viewer using the signed URL
                viewer.addSplatScene(url, {
                    format: format,
                    splatAlphaRemovalThreshold: 5,
                    showLoadingUI: false,
                    position: [0, 0, 0],
                    rotation: [1, 0, 0, 0],
                    progressiveLoad: false,
                    scale: [1, 1, 1],
                }).then(() => {
                    return new Promise(resolve => requestAnimationFrame(resolve)) // Make it wait a frame before adding collision mesh so the initial camera position is close up and not far away
                }).then(() => {
                    this.createPreciseCollisionMesh(viewer)
                })

                const group = new THREE.Group<THREE.Object3DEventMap>()
                if (viewer) {
                    group.add(viewer)
                }
                return group
            }),
            catchError((err) => {
                this._toastService.toast({
                    title: 'Error',
                    message: 'Error Loading Splat Model',
                    color: 'red'
                })
                return EMPTY
            })
        )
    }
}
